import {
    acceptIncomingDebinCVUSendMiddleware,
    acceptIncomingDebinPreMiddleware,
    acceptIncomingDebinSendMiddleware,
    cancelDebin,
    chargebackIncomingDebinSendMiddleware,
    chargebackPullIncomingDebinSendMiddleware,
    createCredin,
    createCredinCVU,
    createCredinPre,
    createDebin,
    createDebinPre,
    declineIncomingDebinSendMiddleware,
    getDebinRecurrencyDetail,
    getProgrammedRecurrencies,
    getRecurrencies,
    getSeller,
    listConcepts,
    loadIncomingDebinListMiddleware,
    loadSellerDebinDetail,
    loadSentRequestsMiddleware,
    relateBuyerToRecurrency,
    removeSellerAccountMiddleware,
    sendAddAccountsAction,
    setUpdateRecurrencyState,
} from "middleware/debin";
import * as session from "middleware/session";
import { push, routerActions } from "react-router-redux";
import { actions as debinActions, selectors as debinSelectors, types } from "reducers/debin";
import { actions as formActions } from "reducers/form";
import { actions as notificationActions } from "reducers/notification";
import { all, call, put, select, take, takeLatest } from "redux-saga/effects";
import { hasTransactionHistoryWithCbu } from "sagas/transactions";
import * as configUtils from "util/config";
import {
    getCredinCvuTransactionId,
    getCredinActivityId,
    assignCredinDraftIdsForSplit,
    isValidCVU,
    shouldDeleteIncompatibleCredinDraft,
    separarCredinAndCbuList,
    separarRecipients,
} from "util/credin";
import * as utilDownload from "util/download";
import * as i18n from "util/i18n";
import { getGenerateCredinPath, getRequestCredinPath } from "util/v3UxConfig";
import { INSUFFICIENT_SIGNATURES, getInsufficientBalanceSnackbar, isInsufficientBalance } from "util/responses";

const sagas = [
    takeLatest(types.LOAD_SENT_REQUESTS_REQUEST, loadSentRequests),
    takeLatest(types.SEND_ADD_ACCOUNT_ACTION_REQUEST, sendAddAccountAction),
    takeLatest(types.CANCEL_DEBIN_REQUEST, cancelDebinRequest),
    takeLatest(types.LOAD_INCOMING_DEBIN_LIST_REQUEST, loadIncomingDebinList),
    takeLatest(types.REMOVE_SELLER_ACCOUNT_REQUEST, removeSellerAccount),
    takeLatest(types.ACCEPT_INCOMING_DEBIN_PRE_REQUEST, acceptIncomingDebinPreRequest),
    takeLatest(types.ACCEPT_INCOMING_DEBIN_PRE_REQUEST_REDIRECT, acceptIncomingDebinPreRequestRedirect),
    takeLatest(types.ACCEPT_INCOMING_DEBIN_PRE_REQUEST_PENDING, acceptIncomingDebinPreRequestPending),
    takeLatest(types.DEBIN_SEND_REQUEST, sendDebinCreateRequest),
    takeLatest(types.SEND_DEBIN_PRE_REQUEST, sendDebinCreatePreRequest),
    takeLatest(types.ACCEPT_INCOMING_DEBIN_SEND_REQUEST, acceptIncomingDebinSendRequest),
    takeLatest(types.DECLINE_INCOMING_DEBIN_SEND_REQUEST, declineIncomingDebinSendRequest),
    takeLatest(types.CHARGEBACK_INCOMING_DEBIN_SEND_REQUEST, chargebackIncomingDebinSendRequest),
    takeLatest(types.CHARGEBACK_PULL_INCOMING_DEBIN_SEND_REQUEST, chargebackPullIncomingDebinSendRequest),
    takeLatest(types.SEND_CREDIN_PRE_REQUEST, sendCredinCreatePreRequest),
    takeLatest(types.SEND_CREDIN_REQUEST, sendCredinCreateRequest),
    takeLatest(types.GET_SELLER_DEBIN_DETAIL_REQUEST, getSellerDebinDetail),
    takeLatest(types.DECLINE_FIXED_TERM_SEND_REQUEST, declineFixedTermSendRequest),
    takeLatest(types.DEBIN_PRE_REQUEST, debinPreRequest),
    takeLatest(types.DEBIN_SELLER_CUIT, getSellerInfo),
    takeLatest(types.RELATE_BUYER_RECURRENCY, relateBuyerRecurrency),
    takeLatest(types.DEBIN_RECURRENCIES, getDebinRecurrencies),
    takeLatest(types.DEBIN_PROGRAMMED_RECURRENCIES, getDebinProgrammedRecurrencies),
    takeLatest(types.DEBIN_PROGRAMMED_RECURRENCIES_MORE, getDebinProgrammedRecurrenciesMore),
    takeLatest(types.SET_UPDATE_RECURRENCY_STATE, updateRecurrencyState),
    takeLatest(types.SET_IS_BACK_FROM_SUMMARY, setIsBackFromSummary),
    takeLatest(types.PUTNOTIFICATION, putNotification),
    takeLatest(types.GET_DEBIN_RECURRENCY_DETAIL_REQUEST, getDebinRecurrencyDetailSaga),
];

export default sagas;

function* updateRecurrencyState({ debin, setSubmitting }) {
    try {
        const response = yield call(setUpdateRecurrencyState, debin);
        const debinStateMessage = debin?.action?.toLowerCase();
        const isProgrammed = debin?.limite_cuotas;

        if (response.type === "W" && response.data.code !== "API516W" && response.data.code !== "COR020W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        } else if (response.data.code === "COR023I") {
            yield put({
                type: types.CLEAN_DEBIN_RECURRENCIES,
            });

            if (isProgrammed) {
                yield put({
                    type: types.CLEAN_DEBIN_PROGRAMMED_RECURRENCIES,
                });
                yield put(routerActions.push("/v2/debin/scheduled"));
                yield put(
                    notificationActions.showNotification(
                        i18n.get(`debin.scheduled.${debinStateMessage}.pending`),
                        "warning",
                        ["debin.recurrency", "debin.scheduled"],
                    ),
                );
            } else {
                yield put(routerActions.push("/payments/recurrent"));
                yield put(
                    notificationActions.showNotification(
                        i18n.get(`debin.recurrency.${debinStateMessage}.pending`),
                        "warning",
                        ["debin.recurrency", "debin.scheduled"],
                    ),
                );
            }
        } else if (response.data.code === "API516W") {
            const transferFunctionalities = configUtils.getBoolean(
                "frontend.show.failedVoucher.transfersFunctionalities",
                false,
            );
            const recurringDebinV3 = configUtils.getBoolean("frontend.show.RecurringDebin.functionalities", false);
            let notificationMessage;
            if (transferFunctionalities || recurringDebinV3) {
                const error = response.data.data.voucherErrorCode;
                const message = response.data.data.voucherErrorMessage;
                notificationMessage = i18n.get("notification.snackbar.failed.voucher", null, {
                    VOUCHER_MESSAGE: message,
                    VOUCHER_ERROR: error,
                });
            } else {
                notificationMessage = i18n.get("debin.recurrencia.generic.error");
            }
            if (isProgrammed) {
                yield put(routerActions.push("/v2/debin/scheduled"));
                yield put(
                    notificationActions.showNotification(response.data.data.voucherErrorMessage, "error", [
                        "debin",
                        "confirmDebin",
                    ]),
                );
            } else {
                yield put(routerActions.push("/payments/recurrent"));
            }
            yield put(
                notificationActions.showNotification(notificationMessage, "error", [
                    "debin.recurrency",
                    "debin.scheduled",
                ]),
            );
        } else if (response.data.code === "COR020W") {
            yield put(
                notificationActions.showNotification(response.data.data.message, "warning", ["debin", "confirmDebin"]),
            );
            yield put(routerActions.push("/v2/debin/scheduled"));
        } else {
            yield put({
                type: types.CLEAN_DEBIN_RECURRENCIES,
            });
            if (isProgrammed) {
                yield put({
                    type: types.CLEAN_DEBIN_PROGRAMMED_RECURRENCIES,
                });
                yield put(routerActions.push("/v2/debin/scheduled"));
                yield put(
                    notificationActions.showNotification(
                        i18n.get(`debin.scheduled.${debinStateMessage}.success`),
                        "success",
                        ["debin.recurrency", "debin.scheduled"],
                    ),
                );
            } else {
                yield put(routerActions.push("/payments/recurrent"));
                yield put(
                    notificationActions.showNotification(
                        i18n.get(`debin.recurrency.${debinStateMessage}.success`),
                        "success",
                        ["debin.recurrency", "debin.scheduled"],
                    ),
                );
            }
        }
    } finally {
        setSubmitting(false);
    }
}

function* getDebinRecurrencies({ formFilter, setSubmitting }) {
    try {
        const response = yield call(getRecurrencies, formFilter);
        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        } else if (response.data.data?.error) {
            yield put({
                type: types.CLEAN_DEBIN_RECURRENCIES,
            });
        } else {
            yield put({
                type: types.SET_DEBIN_RECURRENCIES,
                debinRecurrency: response.data.data?.recurrencies,
                paginas_totales: response.data.data?.paginas_totales,
            });
        }
    } finally {
        setSubmitting(false);
    }
}

function* getDebinProgrammedRecurrencies({ formFilter }) {
    try {
        const response = yield call(getProgrammedRecurrencies, formFilter);
        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        } else if (response.data.data?.error) {
            yield put({
                type: types.CLEAN_DEBIN_PROGRAMMED_RECURRENCIES,
            });
        } else {
            yield put({
                type: types.SET_DEBIN_PROGRAMMED_RECURRENCIES,
                debinProgrammedRecurrency: response.data.data?.recurrencies,
                paginas_totales: response.data.data?.paginas_totales,
            });
        }
    } finally {
        // setSubmitting(false); // TODO
    }
}

function* getDebinProgrammedRecurrenciesMore({ formFilter }) {
    try {
        const response = yield call(getProgrammedRecurrencies, formFilter);
        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        } else if (response.data.data?.error) {
            // No limpiar la lista en caso de error para mantener los datos existentes
        } else {
            yield put({
                type: types.SET_DEBIN_PROGRAMMED_RECURRENCIES_MORE,
                debinProgrammedRecurrency: response.data.data?.recurrencies,
                paginas_totales: response.data.data?.paginas_totales,
            });
        }
    } finally {
        // setSubmitting(false); // TODO
    }
}

function* relateBuyerRecurrency({ debinPrestacion, setSubmitting }) {
    try {
        const response = yield call(relateBuyerToRecurrency, debinPrestacion);

        if (response.type === "W" && response.data.code !== "API516W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        } else {
            if (response.data.code !== "API516W" && response.data.code === INSUFFICIENT_SIGNATURES) {
                yield put(
                    notificationActions.showNotification(i18n.get("debin.recurrency.activate.pending"), "success", [
                        "debin.ticket",
                    ]),
                );
            } else if (response.data.code !== "API516W" && response.data.code !== INSUFFICIENT_SIGNATURES) {
                yield put(
                    notificationActions.showNotification(i18n.get("debin.recurrency.activate.success"), "success", [
                        "debin.ticket",
                    ]),
                );
            }
            yield put(
                formActions.readTransaction({
                    pathname: `/transaction/${response.data.idTransaction}`,
                    newTransaction: true,
                }),
            );
            yield take("READ_TRANSACTION_REQUEST"); // waiting until finish action
        }
    } finally {
        setSubmitting(false);
    }
}

function* getSellerInfo({ seller, page }) {
    const response = yield call(getSeller, seller);
    const FORM_ID = "debin.recurrency";
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        yield put({ type: types.CANCEL_DEBIN_FAILURE });
    } else if (response.data.data?.error && page === FORM_ID) {
        yield put(notificationActions.showNotification(response.data.data.message, "error", ["debin", "confirmDebin"]));
        yield put({ type: types.CLEAN_DEBIN_SELLER });
    } else if (response.data.data?.error && page !== FORM_ID) {
        yield put({ type: types.CLEAN_DEBIN_SELLER });
    } else {
        yield put({
            type: types.LOAD_DEBIN_SELLER,
            seller: response.data.data?.seller,
            prestaciones: response.data.data?.prestaciones,
        });
    }
}

function* loadSentRequests({ filters }) {
    const response = yield call(loadSentRequestsMiddleware, filters);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        yield put({ type: types.LOAD_SENT_REQUESTS_FAILURE });
    } else {
        yield put({
            type: types.LOAD_SENT_REQUESTS_SUCCESS,
            sentRequests: response.data.data.sentRequests,
            totalPages: parseInt(response.data.data.totalPages, 10),
        });
    }
}

function* sendAddAccountAction({ otp, accountToChange, userId, formikBag }) {
    const response = yield call(sendAddAccountsAction, otp, accountToChange.idProduct, userId);
    if (response.type === "W") {
        if (response.data.code === "COR020W") {
            formikBag.setErrors(response.data.data);
        } else {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        }
        yield put({ type: types.SEND_ADD_ACCOUNT_ACTION_FAILURE });
    } else {
        yield put(routerActions.push("/charges/debin/addAccounts"));
        yield put(
            notificationActions.showNotification(i18n.get("debin.addAccount.success.message"), "success", [
                "debinAccounts",
            ]),
        );
    }
}

function* cancelDebinRequest({ otp, debinToCancel, formikBag }) {
    const response = yield call(cancelDebin, otp, debinToCancel);

    if (response.type === "W") {
        if (response.data.code === "COR020W") {
            formikBag.setErrors(response.data.data);
        } else {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debinCancel"]));
        }
        yield put({ type: types.CANCEL_DEBIN_FAILURE });
    } else {
        yield put({
            type: types.CANCEL_DEBIN_SUCCESS,
        });
        yield put(routerActions.push("/charges/generateDebin"));
        yield put(
            notificationActions.showNotification(i18n.get("debin.cancel.success.message"), "success", [
                "debin",
                "confirmDebin",
            ]),
        );
    }
}

function* loadIncomingDebinList({ filters, isMobile }) {
    const response = yield call(loadIncomingDebinListMiddleware, filters);

    if (response.type === "W" || response.data.code === "COR120I") {
        if (response.data.code !== "COR120I") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        }
        yield put({ type: types.LOAD_INCOMING_DEBIN_LIST_FAILURE });
    } else {
        let totalSentRequests = response.data.data.sentRequests;
        if (isMobile) {
            const sentRequests = yield select((state) => debinSelectors.getSentRequests(state));

            if (sentRequests?.debinList?.length > 0) {
                totalSentRequests = {
                    ...totalSentRequests,
                    debinList: [...sentRequests?.debinList, ...totalSentRequests?.debinList],
                };
            }
        }
        yield put({
            type: types.LOAD_INCOMING_DEBIN_LIST_SUCCESS,
            sentRequests: totalSentRequests,
            totalPages: parseInt(response.data.data.totalPages, 10),
        });
    }
}

function* removeSellerAccount({ accountToChange }) {
    const response = yield call(removeSellerAccountMiddleware, accountToChange.idProduct);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        yield put({ type: types.REMOVE_SELLER_ACCOUNT_FAILURE });
    } else {
        yield put(routerActions.push("/charges/debin/addAccounts"));
        yield put(
            notificationActions.showNotification(i18n.get("debin.removeAccount.success.message"), "success", [
                "debin",
                "confirmDebin",
            ]),
        );
        yield put({
            type: types.REMOVE_SELLER_ACCOUNT_SUCCESS,
        });
    }
}

function* acceptIncomingDebinPreRequest({ idDebin }) {
    const response = yield call(acceptIncomingDebinPreMiddleware, idDebin);
    if (response.data.code === "COR120I") {
        yield put(routerActions.push("/payments/payDebin"));
        yield put(
            notificationActions.showNotification(response.data.data.messageError, "warning", ["debin", "confirmDebin"]),
        );
        yield put({ type: types.ACCEPT_INCOMING_DEBIN_PRE_FAILURE });
    }
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        yield put({ type: types.ACCEPT_INCOMING_DEBIN_PRE_FAILURE });
    } else {
        const { newRecipient, debinDetail } = response.data?.data || {};

        yield put({
            type: types.ACCEPT_INCOMING_DEBIN_PRE_SUCCESS,
            debinDetail,
            control: response.data?.data?.control,
            newRecipient,
        });
    }
}

function* acceptIncomingDebinPreRequestRedirect({ idDebin, route }) {
    const response = yield call(acceptIncomingDebinPreMiddleware, idDebin);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        yield put({ type: types.ACCEPT_INCOMING_DEBIN_PRE_FAILURE });
    } else {
        const { newRecipient, debinDetail } = response.data?.data || {};

        yield put({
            type: types.ACCEPT_INCOMING_DEBIN_PRE_SUCCESS,
            debinDetail,
            control: response.data?.data?.control,
            newRecipient,
        });

        yield put(push(route));
        return;
    }

    yield put(push(route));
}

function* acceptIncomingDebinPreRequestPending({ idTransaction, isRecurrency = false }) {
    yield put(
        formActions.readTransaction({
            pathname: `/transaction/${idTransaction}`,
            newTransaction: true,
        }),
    );

    yield put(
        notificationActions.showNotification(i18n.get("pendingDebin.snackbar.text"), "warning", [
            isRecurrency ? "debinRecurringTransaction" : "debinTransaction",
        ]),
    );

    yield take("READ_TRANSACTION_REQUEST");
}

function* getSellerDebinDetail({ idDebin }) {
    const response = yield call(loadSellerDebinDetail, idDebin);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
        yield put({ type: types.GET_SELLER_DEBIN_DETAIL_FAILURE });
    } else {
        yield put({
            type: types.GET_SELLER_DEBIN_DETAIL_SUCCESS,
            debinDetail: response.data.data.debinDetail,
        });
    }
}

function* sendDebinCreateRequest({ debinSeller, recipients, otp, formikBag }) {
    const { date } = debinSeller;
    const debin = {
        expirationDate: date,
        ...debinSeller,
    };
    const response = yield call(createDebin, otp, debin, recipients);

    if (response.type === "W") {
        if (response.data.code === "COR020W") {
            formikBag.setErrors(response.data.data);
        } else {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debinSummary"]));
        }
        yield put({ type: types.DEBIN_SEND_FAILURE });
    } else if (response.data.code === "COR120I") {
        yield put({ type: types.DEBIN_SEND_FAILURE });
        yield put(
            notificationActions.showNotification(i18n.get("debin.massive.error"), "warning", ["debin", "confirmDebin"]),
        );
        yield put(routerActions.push("/charges/generateDebin"));
    } else {
        yield put({
            type: types.DEBIN_SEND_SUCCESS,
        });

        if (response.data.data.frequentDestination) {
            yield put(
                notificationActions.showNotification(i18n.get("debin.create.success.message.frequent"), "success", [
                    "debin",
                ]),
            );
        } else {
            yield put(
                notificationActions.showNotification(i18n.get("debin.create.success.message"), "success", [
                    "debin",
                    "confirmDebin",
                ]),
            );
        }
        yield put(routerActions.push("/charges/generateDebin"));
    }
}

function* sendDebinCreatePreRequest({ debinSeller, cbuList }) {
    const { date, concept, cbu, amount, idAccount } = debinSeller;
    const debin = {
        expirationDate: date,
        concept,
        cbu,
        amount,
        idAccount,
    };
    const response = yield call(createDebinPre, debin, cbuList);
    if (response.type === "W" && response.data.code !== "API516W") {
        const message = response?.data?.data?.message || response?.data?.message;
        yield put(notificationActions.showNotification(message, "error", ["debin", "confirmDebin"]));
        yield put({ type: types.SEND_DEBIN_PRE_FAILURE });
    } else if (response.data.code === "COR120I") {
        yield put({ type: types.SEND_DEBIN_PRE_FAILURE });
        yield put(notificationActions.showNotification(response.data.data.message, "error", ["debin", "confirmDebin"]));
    } else {
        yield put({
            type: types.SEND_DEBIN_PRE_SUCCESS,
            recipients: response.data.data.recipients,
        });
        yield put(routerActions.push("requestDebin/summary"));
    }
}

function* sendCredinCreatePreRequest({ debinSeller, cbuList }) {
    const { concept, cbu, amount, idAccount } = debinSeller;
    const credin = {
        concept,
        cbu,
        amount,
        idAccount,
    };
    const response = yield call(createCredinPre, credin, cbuList);
    if (response.type === "W" && response.data.code !== "API516W") {
        const message = response?.data?.data?.message || response?.data?.message;
        yield put(notificationActions.showNotification(message, "error", ["credin"]));
        yield put({ type: types.SEND_CREDIN_PRE_FAILURE });
    } else if (response.data.code === "COR001E") {
        yield put({ type: types.SEND_CREDIN_PRE_FAILURE });
        yield put(notificationActions.showNotification(response.data.data.message, "error", ["credin"]));
    } else {
        const { newRecipient } = response.data?.data || {};
        yield put({
            type: types.SEND_CREDIN_PRE_SUCCESS,
            recipients: response.data.data.recipients,
            id: response.data.data.id,
            control: response.data?.data?.control,
            newRecipient,
        });
        yield put(routerActions.push(getGenerateCredinPath()));
    }
}

function* showCredinMultiRecipientSuccessNotification(response) {
    if (isInsufficientBalance(response.data.code)) {
        const pendingMessage = configUtils.get(
            "frontend.transfers.insufficientbalance.snackbar",
            "Atención. Tu transacción quedó pendiente por saldo insuficiente",
        );
        const { message, type: notificationType } = getInsufficientBalanceSnackbar(response.data, pendingMessage);
        yield put(notificationActions.showNotification(message, notificationType, ["credin"]));
        return;
    }
    if (response.data.code === INSUFFICIENT_SIGNATURES) {
        yield put(
            notificationActions.showNotification(i18n.get("credin.create.pending.message"), "success", ["credin"]),
        );
        return;
    }
    const frequentDestinationMessage = response.data.data?.frequentDestination
        ? "credin.create.success.message.frequent"
        : "credin.create.success.message";
    yield put(notificationActions.showNotification(i18n.get(frequentDestinationMessage), "success", ["credin"]));
}

function* handleSuccessResponse(response, recipients, continuar) {
    if (recipients?.length > 1 && continuar) {
        yield put(routerActions.push(getRequestCredinPath()));
        yield put({ type: types.CREDIN_SEND_SUCCESS });
        yield call(showCredinMultiRecipientSuccessNotification, response);
    } else {
        const newRecipient = yield select((state) => debinSelectors.getNewRecipient(state));

        if (isInsufficientBalance(response.data.code)) {
            const pendingMessage = configUtils.get(
                "frontend.transfers.insufficientbalance.snackbar",
                "Atención. Tu transacción quedó pendiente por saldo insuficiente",
            );
            const { message, type: notificationType } = getInsufficientBalanceSnackbar(response.data, pendingMessage);
            yield put(notificationActions.showNotification(message, notificationType, ["forms"]));
        }
        yield put(
            formActions.readTransaction({
                pathname: `/transaction/${response.data.idTransaction}`,
                newTransaction: true,
            }),
        );

        if (newRecipient && recipients?.length === 1) {
            const cbu = recipients[0]?.cbu;
            const isCVU = cbu?.startsWith("000");
            const idActivity = isCVU ? "credinCVU.sentRequest.send" : "credin.sentRequest.send";
            const hasHistory = yield call(hasTransactionHistoryWithCbu, cbu, idActivity);
            if (!hasHistory) {
                yield put(
                    notificationActions.showNotification(
                        i18n.get("transfer.frequentDestinacions.notification.firstTransfer"),
                        "warning",
                        ["forms", "credin"],
                        i18n.get("transfer.frequentDestinacions.notification.firstTransfer.tittle"),
                    ),
                );
            }
        }

        yield take("READ_TRANSACTION_REQUEST");
        yield put(debinActions.setDebinSeller({}, []));
    }
}

function* validateResponse(response, recipients, setErrors, continuar) {
    const { type, data } = response;

    if (continuar && type === "W" && data.code !== "API516W" && data.code !== "COR020W") {
        yield put(notificationActions.showNotification(data.message, "error", ["debin", "confirmDebin"]));

        yield put({ type: types.CREDIN_SEND_FAILURE });
    } else if ((continuar && data.code === "COR999E") || data.code === "COR020W") {
        yield put({ type: types.CREDIN_SEND_FAILURE });
        setErrors(data.data);

        if (data.code !== "COR020W") {
            yield put(notificationActions.showNotification(data.data.message, "error", ["debin", "confirmDebin"]));
        }
    } else {
        yield call(handleSuccessResponse, response, recipients, continuar);
    }
}

const isCredinSendFailure = (response) => {
    const { type, data } = response;
    return (
        (type === "W" && data.code !== "API516W" && data.code !== "COR020W") ||
        data.code === "COR999E" ||
        data.code === "COR020W"
    );
};

function* handleMixedCredinFailure(response, setErrors, notifyError) {
    const { type, data } = response;

    if (type === "W" && data.code !== "API516W" && data.code !== "COR020W" && data.code !== "COR999E") {
        if (notifyError) {
            yield put(notificationActions.showNotification(data.message, "error", ["debin", "confirmDebin"]));
        }
        return;
    }

    setErrors(data.data);
    if (notifyError && data.code !== "COR020W") {
        yield put(
            notificationActions.showNotification(data.data?.message || data.message, "error", [
                "debin",
                "confirmDebin",
            ]),
        );
    }
}

function* handleMixedCredinResponses(responses, setErrors) {
    const [cbuResponse, cvuResponse] = responses;
    const hasCbuFailure = isCredinSendFailure(cbuResponse);
    const hasCvuFailure = isCredinSendFailure(cvuResponse);
    const hasPartialFailure = hasCbuFailure !== hasCvuFailure;
    const notifyFailure = !hasPartialFailure;

    if (hasCbuFailure) {
        yield* handleMixedCredinFailure(cbuResponse, setErrors, notifyFailure);
    }
    if (hasCvuFailure) {
        yield* handleMixedCredinFailure(cvuResponse, setErrors, notifyFailure);
    }

    if (hasCbuFailure && hasCvuFailure) {
        yield put({ type: types.CREDIN_SEND_FAILURE });
        return;
    }

    if (hasPartialFailure) {
        yield put(routerActions.push(getRequestCredinPath()));
        yield put({ type: types.CREDIN_SEND_SUCCESS });
        yield put(
            notificationActions.showNotification(i18n.get("credin.confirmation.partialWarning"), "warning", [
                "debin",
                "confirmDebin",
            ]),
        );
        return;
    }

    const successfulResponse = hasCbuFailure ? cvuResponse : cbuResponse;
    yield put(routerActions.push(getRequestCredinPath()));
    yield put({ type: types.CREDIN_SEND_SUCCESS });
    yield call(showCredinMultiRecipientSuccessNotification, successfulResponse);
}

export function* sendCredinCBUCVUCreateRequest({
    debinSeller,
    recipients,
    otp,
    id,
    formikBag,
    draftTransactionIdToDelete,
    draftActivityId,
}) {
    const { setSubmitting, setErrors } = formikBag;

    try {
        const credin = { ...debinSeller };
        const cbuList = formikBag?.props?.cbuList;

        const { recipientsCVU, recipientsCBU } = separarRecipients(recipients);
        const { credinCVU, credinCBU, cbuListCVU, cbuListCBU } = separarCredinAndCbuList(credin, cbuList);
        const idForCredinCVU = getCredinCvuTransactionId(id, recipientsCBU);
        const isMixedRequest = recipientsCBU.length > 0 && recipientsCVU.length > 0;
        const splitRequests = isMixedRequest
            ? assignCredinDraftIdsForSplit(credinCBU, credinCVU, draftActivityId)
            : { credinCBU, credinCVU };
        let continuar = false;
        if (isMixedRequest) {
            const responses = yield all([
                call(createCredin, otp, splitRequests.credinCBU, recipientsCBU, id, cbuListCBU, null),
                call(createCredinCVU, otp, splitRequests.credinCVU, recipientsCVU, idForCredinCVU, cbuListCVU, null),
            ]);

            yield* handleMixedCredinResponses(responses, setErrors);
            return;
        }

        if (recipientsCBU.length > 0) {
            const response = yield call(
                createCredin,
                otp,
                splitRequests.credinCBU,
                recipientsCBU,
                id,
                cbuListCBU,
                recipientsCVU.length === 0 ? draftTransactionIdToDelete : null,
            );
            const { type, data } = response;
            if (type === "W" && data.code !== "API516W" && data.code !== "COR020W") {
                yield put(notificationActions.showNotification(data.message, "error", ["debin", "confirmDebin"]));

                yield put({ type: types.CREDIN_SEND_FAILURE });
                return;
            }
            if (data.code === "COR999E" || data.code === "COR020W") {
                yield put({ type: types.CREDIN_SEND_FAILURE });
                setErrors(data.data);

                if (data.code !== "COR020W") {
                    yield put(
                        notificationActions.showNotification(data.data.message, "error", ["debin", "confirmDebin"]),
                    );
                }
                return;
            }
            if (recipients?.length > 1) {
                yield put(routerActions.push(getRequestCredinPath()));
                yield put({ type: types.CREDIN_SEND_SUCCESS });

                if (isInsufficientBalance(response.data.code) || response.data.code === INSUFFICIENT_SIGNATURES) {
                    yield call(showCredinMultiRecipientSuccessNotification, response);
                } else if (recipientsCVU.length === 0) {
                    yield call(showCredinMultiRecipientSuccessNotification, response);
                } else {
                    continuar = true;
                }
            } else {
                if (isInsufficientBalance(response.data.code)) {
                    const pendingMessage = configUtils.get(
                        "frontend.transfers.insufficientbalance.snackbar",
                        "Atención. Tu transacción quedó pendiente por saldo insuficiente",
                    );
                    const { message, type: notificationType } = getInsufficientBalanceSnackbar(
                        response.data,
                        pendingMessage,
                    );
                    yield put(notificationActions.showNotification(message, notificationType, ["forms"]));
                }
                yield put(
                    formActions.readTransaction({
                        pathname: `/transaction/${response.data.idTransaction}`,
                        newTransaction: true,
                    }),
                );
                yield take("READ_TRANSACTION_REQUEST");
                yield put(debinActions.setDebinSeller({}, []));
            }
        }

        if (recipientsCVU.length > 0) {
            const response = yield call(
                createCredinCVU,
                otp,
                splitRequests.credinCVU,
                recipientsCVU,
                idForCredinCVU,
                cbuListCVU,
                recipientsCBU.length === 0 ? draftTransactionIdToDelete : null,
            );

            yield call(validateResponse, response, recipients, setErrors, continuar);
        }
    } finally {
        setSubmitting(false);
    }
}

export function* syncCredinDraftActivity({ debinSeller, requiredActivity, recipients }) {
    const idTransaction = debinSeller?.idTransaction;
    if (!idTransaction) {
        return {
            debinSeller,
            draftTransactionIdToDelete: null,
            draftActivityId: null,
        };
    }

    const draftActivity = yield select(debinSelectors.getCredinDraftActivity);
    if (!draftActivity || draftActivity.idTransaction !== idTransaction) {
        return {
            debinSeller,
            draftTransactionIdToDelete: null,
            draftActivityId: null,
        };
    }
    if (!shouldDeleteIncompatibleCredinDraft(draftActivity.idActivity, requiredActivity, recipients)) {
        return {
            debinSeller,
            draftTransactionIdToDelete: null,
            draftActivityId: draftActivity.idActivity,
        };
    }

    return {
        debinSeller: {
            ...debinSeller,
            idTransaction: null,
        },
        draftTransactionIdToDelete: idTransaction,
        draftActivityId: null,
    };
}

export function* sendCredinCreateRequest({ debinSeller, recipients, otp, id, formikBag }) {
    const isCredinCVUSignatureActive = configUtils.getBoolean(
        "frontend.esquemaFirmas.validation.credinCVU.enable",
        false,
    );
    const cbuList = formikBag?.props?.cbuList;
    const requiredActivity = getCredinActivityId(recipients, isCredinCVUSignatureActive);
    const { debinSeller: debinSellerToSend, draftTransactionIdToDelete, draftActivityId } = yield call(
        syncCredinDraftActivity,
        {
            debinSeller,
            requiredActivity,
            recipients,
        },
    );

    if (isCredinCVUSignatureActive) {
        yield call(sendCredinCBUCVUCreateRequest, {
            debinSeller: debinSellerToSend,
            recipients,
            otp,
            id,
            formikBag,
            draftTransactionIdToDelete,
            draftActivityId,
        });
        return;
    }

    const { setSubmitting, setErrors } = formikBag;
    try {
        const credin = {
            ...debinSellerToSend,
        };

        const response = yield call(createCredin, otp, credin, recipients, id, cbuList, draftTransactionIdToDelete);
        if (response.type === "W" && response.data.code !== "API516W" && response.data.code !== "COR020W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]));
            yield put({ type: types.CREDIN_SEND_FAILURE });
        } else if (response.data.code === "COR999E" || response.data.code === "COR020W") {
            yield put({ type: types.CREDIN_SEND_FAILURE });
            setErrors(response.data.data);
            if (response.data.code !== "COR020W") {
                yield put(
                    notificationActions.showNotification(response.data.data.message, "error", [
                        "debin",
                        "confirmDebin",
                    ]),
                );
            }
        } else if (recipients?.length > 1) {
            yield put(routerActions.push(getRequestCredinPath()));

            yield put({
                type: types.CREDIN_SEND_SUCCESS,
            });
            yield call(showCredinMultiRecipientSuccessNotification, response);
        } else {
            yield put(
                formActions.readTransaction({
                    pathname: `/transaction/${response.data.idTransaction}`,
                    newTransaction: true,
                }),
            );
            yield take("READ_TRANSACTION_REQUEST");
            yield put(debinActions.setDebinSeller({}, []));
        }
    } finally {
        setSubmitting(false);
    }
}

function* acceptIncomingDebinSendRequest({
    currency,
    amount,
    idDebin,
    otp,
    account,
    userId,
    formikBag,
    sellerDetail,
    idVoucher,
    isOwn,
}) {
    const { setSubmitting, setErrors } = formikBag;

    try {
        const isDebinCVUSignatureActive = configUtils.getBoolean("frontend.esquemaFirmas.validation.debinCVU.enable");

        const isValidCVUSeller = isValidCVU(sellerDetail.cbu);

        const response = yield call(
            isDebinCVUSignatureActive && isValidCVUSeller
                ? acceptIncomingDebinCVUSendMiddleware
                : acceptIncomingDebinSendMiddleware,
            currency,
            amount,
            idDebin,
            otp,
            account?.idProduct,
            userId,
            account,
            sellerDetail,
            idVoucher,
            isOwn,
        );

        if (response.type === "W" && response.data.code !== "API516W") {
            const errorMessage =
                response.data?.data?.NO_FIELD ||
                response.data?.data?.message ||
                response.data?.message ||
                i18n.get("global.unexpectedError");
            if (response.data.code === "COR020W") {
                setErrors(response.data.data);
                yield put(notificationActions.showNotification(errorMessage, "error", ["debin", "confirmDebin"]));
            } else {
                setErrors(response.data.data);
                yield put(notificationActions.showNotification(errorMessage, "error", ["debin", "confirmDebin"]));
            }
            yield put({ type: types.ACCEPT_INCOMING_DEBIN_SEND_FAILURE });
        } else {
            yield put({
                type: types.ACCEPT_INCOMING_DEBIN_SEND_SUCCESS,
            });
            if (response.data.code !== "API516W") {
                if (response.data.code === INSUFFICIENT_SIGNATURES) {
                    const control = yield select((state) => debinSelectors.getControl(state));
                    if (!control) {
                        yield put(
                            notificationActions.showNotification(i18n.get("accept.incoming.debin.pending"), "success", [
                                "debinTransaction",
                            ]),
                        );
                    }
                } else {
                    yield put(
                        notificationActions.showNotification(i18n.get("accept.incoming.debin.success"), "success", [
                            "debinTransaction",
                        ]),
                    );
                }
            }
            const newRecipient = yield select((state) => debinSelectors.getNewRecipient(state));

            yield put(
                formActions.readTransaction({
                    pathname: `/transaction/${response.data.idTransaction}`,
                    newTransaction: true,
                }),
            );

            if (newRecipient) {
                const sellerCbu = sellerDetail?.cbu;
                const hasHistory = yield call(hasTransactionHistoryWithCbu, sellerCbu, "debin.incoming.accept.send");
                if (!hasHistory) {
                    yield put(
                        notificationActions.showNotification(
                            i18n.get("transfer.frequentDestinacions.notification.firstTransfer"),
                            "warning",
                            ["forms"],
                            i18n.get("transfer.frequentDestinacions.notification.firstTransfer.tittle"),
                        ),
                    );
                }
            }

            yield take("READ_TRANSACTION_REQUEST");
        }
    } finally {
        setSubmitting(false);
    }
}
function* declineIncomingDebinSendRequest({ currency, amount, idDebin, otp, account, userId, formikBag }) {
    const { setSubmitting, setErrors } = formikBag;
    try {
        const response = yield call(
            declineIncomingDebinSendMiddleware,
            currency,
            amount,
            idDebin,
            otp,
            account?.idProduct,
            userId,
        );

        if (response.type === "W") {
            if (response.data.code === "COR020W") {
                setErrors(response.data.data);
            } else {
                yield put(notificationActions.showNotification(response.data.message, "error", ["decline-debin"]));
            }
            yield put({ type: types.DECLINE_INCOMING_DEBIN_FAILURE });
        } else {
            const confirmationMessage = i18n.get("payments.debin.decline.confirmationMessage");
            yield put({ type: types.DECLINE_INCOMING_DEBIN_SUCCESS });
            yield put(routerActions.push("/payments/payDebin"));
            yield put(notificationActions.showNotification(confirmationMessage, "success", ["debin", "confirmDebin"]));
        }
    } finally {
        setSubmitting(false);
    }
}
function* declineFixedTermSendRequest({ currency, amount, idDebin, otp, account, userId, formikBag }) {
    const { setSubmitting, setErrors } = formikBag;
    try {
        const response = yield call(
            declineIncomingDebinSendMiddleware,
            currency,
            amount,
            idDebin,
            otp,
            account.idProduct,
            userId,
        );

        if (response.type === "W") {
            if (response.data.code === "COR020W") {
                setErrors(response.data.data);
            } else {
                yield put(
                    notificationActions.showNotification(response.data.message, "error", ["debin", "confirmDebin"]),
                );
            }
            yield put({ type: types.DECLINE_INCOMING_DEBIN_FAILURE });
        } else {
            yield put({
                type: types.DECLINE_INCOMING_DEBIN_SUCCESS,
            });
            const confirmationMessage = i18n.get("payments.debin.decline.confirmationMessage");
            yield put(notificationActions.showNotification(confirmationMessage, "success", ["debin", "confirmDebin"]));
            yield put(routerActions.push("/fixedTermAcceptance"));
        }
    } finally {
        setSubmitting(false);
    }
}

function* debinPreRequest() {
    const response = yield call(listConcepts);
    if (response.type === "W") {
        yield put({ type: types.DEBIN_PRE_FAILURE });
    } else {
        yield put({
            type: types.DEBIN_PRE_SUCCESS,
            concepts: response.data.data.concepts,
            accounts: response.data.data.accounts,
            limits: response.data.data.limits,
        });
    }
}

function* setIsBackFromSummary({ page }) {
    if (page) {
        yield put(routerActions.push(page));
    }
}

function* putNotification({ format }) {
    const { type, data } = yield call(session.exportTermsAndConditions, format);
    if (type === "W") {
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                "debin",
                "confirmDebin",
            ]),
        );
        yield put({ type: types.PUTNOTIFICATION_FAILURE });
    } else {
        const { content, fileName } = data.data;
        utilDownload.downloadPdf(fileName, content);
        yield put(
            notificationActions.showNotification(i18n.get("global.download.success"), "success", [
                "debin",
                "confirmDebin",
            ]),
        );
        yield put({ type: types.PUTNOTIFICATION_SUCCESS });
    }
}

function* getDebinRecurrencyDetailSaga({ id }) {
    try {
        const response = yield call(getDebinRecurrencyDetail, id);
        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["debinDetail"]));
            yield put({ type: types.GET_DEBIN_RECURRENCY_DETAIL_FAILURE });
        } else {
            yield put({
                type: types.GET_DEBIN_RECURRENCY_DETAIL_SUCCESS,
                recurrencyDetail: response.data.data?.debin,
                recurrencyParts: response.data.data?.installments,
            });
        }
    } catch (error) {
        yield put({ type: types.GET_DEBIN_RECURRENCY_DETAIL_FAILURE });
    }
}

function* chargebackIncomingDebinSendRequest({
    currency,
    amount,
    idDebin,
    buyerCuit,
    buyerCbu,
    sellerCuit,
    sellerCbu,
    otp,
    formikBag,
}) {
    const { setSubmitting, setErrors } = formikBag;

    try {
        const response = yield call(
            chargebackIncomingDebinSendMiddleware,
            currency,
            amount,
            idDebin,
            buyerCuit,
            buyerCbu,
            sellerCuit,
            sellerCbu,
            otp,
        );

        if (response.type === "W" && response.data.code !== "API516W") {
            const errorMessage =
                response.data?.data?.NO_FIELD ||
                response.data?.data?.message ||
                response.data?.message ||
                i18n.get("global.unexpectedError");
            setErrors(response.data.data);
            yield put(notificationActions.showNotification(errorMessage, "error", ["debin", "confirmDebin"]));
            yield put(routerActions.push("/payments/payDebin"));
            yield put({ type: types.CHARGEBACK_INCOMING_DEBIN_FAILURE });
        } else if (response.data.code === "API516W") {
            const message = response.data.data.voucherErrorMessage;
            yield put(notificationActions.showNotification(message, "error", ["debin", "confirmDebin"]));
            yield put(routerActions.push("/payments/payDebin"));
            yield put({ type: types.CHARGEBACK_INCOMING_DEBIN_FAILURE });
        } else if (response.data.code === "COR023I") {
            yield put(notificationActions.showNotification(response.data?.message, "warning", ["debinTransaction"]));
            yield put(
                formActions.readTransaction({
                    pathname: `/transaction/${response.data.idTransaction}`,
                    newTransaction: true,
                }),
            );
            yield take("READ_TRANSACTION_REQUEST");
        } else {
            yield put({
                type: types.CHARGEBACK_INCOMING_DEBIN_SUCCESS,
            });
            if (response.data.code !== "API516W") {
                if (response.data.code === INSUFFICIENT_SIGNATURES) {
                    const control = yield select((state) => debinSelectors.getControl(state));
                    if (!control) {
                        yield put(
                            notificationActions.showNotification(i18n.get("accept.incoming.debin.pending"), "success", [
                                "debinTransaction",
                            ]),
                        );
                    }
                } else {
                    yield put(
                        notificationActions.showNotification(i18n.get("accept.incoming.debin.success"), "success", [
                            "debinTransaction",
                        ]),
                    );
                }
            }
            yield put(
                formActions.readTransaction({
                    pathname: `/transaction/${response.data.idTransaction}`,
                    newTransaction: true,
                }),
            );
            yield take("READ_TRANSACTION_REQUEST");
        }
    } finally {
        setSubmitting(false);
    }
}

function* chargebackPullIncomingDebinSendRequest({ currency, amount, idDebin, reason, formikBag, debinDetail }) {
    const { setSubmitting, setErrors } = formikBag;

    try {
        const response = yield call(
            chargebackPullIncomingDebinSendMiddleware,
            currency,
            amount,
            idDebin,
            reason,
            debinDetail,
        );

        if (response.type === "W" && response.data.code !== "API516W") {
            const errorMessage =
                response.data?.data?.NO_FIELD ||
                response.data?.data?.message ||
                response.data?.message ||
                i18n.get("global.unexpectedError");
            setErrors(response.data.data);
            yield put(notificationActions.showNotification(errorMessage, "error", ["debin", "confirmDebin"]));
            yield put(routerActions.push("/payments/payDebin"));
        } else if (response.data.code === "API516W") {
            const message = response.data.data.voucherErrorMessage;
            yield put(notificationActions.showNotification(message, "error", ["debin", "confirmDebin"]));
            yield put(routerActions.push("/payments/payDebin"));
        } else if (response.data.code === "COR023I") {
            yield put(
                notificationActions.showNotification(response.data?.message, "warning", ["debin", "confirmDebin"]),
            );
            yield put(routerActions.push("/payments/payDebin"));
        } else {
            if (response.data.code !== "API516W") {
                if (response.data.code === INSUFFICIENT_SIGNATURES) {
                    const control = yield select((state) => debinSelectors.getControl(state));
                    if (!control) {
                        yield put(
                            notificationActions.showNotification(
                                i18n.get("request.chargeback.pull.success"),
                                "success",
                                "debin",
                            ),
                        );
                    }
                } else {
                    yield put(
                        notificationActions.showNotification(i18n.get("request.chargeback.pull.success"), "success", [
                            "debin",
                        ]),
                    );
                }
            }
            yield put(routerActions.push("/payments/payDebin"));
        }
    } finally {
        setSubmitting(false);
    }
}
